import React, { FC, useEffect, useState } from 'react';
import { Collapse } from 'react-collapse';
import { Box, Typography } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import Lottie from 'lottie-react';
import { useStyles } from './styles';
import { ReactComponent as ArrowSVG } from './assets/arrow.svg';
import { RecommendationBoxProps } from './interface';
import loader from './assets/loader.json';
import useGetEvents from '../hook/use-get-events';

export const RECOMMENDATION_PARAM = 'product_assessment_completed';

export const RecommendationBox: FC<RecommendationBoxProps> = ({
  header,
  subTitle,
  subTitleCollapsed,
  children,
  onToggle,
  open,
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const classes = useStyles({ open: collapsed });
  const { t } = useTranslation();
  const [delay, setDelay] = useState(true);
  const { isLoading, isSuccess, recommendationBox } = useGetEvents();

  const { search } = useLocation();
  const history = useHistory();

  const onOpen = () => {
    onToggle();

    if (!open) {
      setTimeout(() => {
        setCollapsed(true);
      }, 20);
    }
  };

  useEffect(() => {
    if (recommendationBox.length > 0) {
      setTimeout(() => setDelay(false), 1500);
    }
  }, [recommendationBox]);

  useEffect(() => {
    if (isSuccess && recommendationBox) {
      const params = new URLSearchParams(search);
      params.delete(RECOMMENDATION_PARAM);

      history.replace({
        search: params.toString(),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <Box>
      <Box onClick={onOpen} className={classes.bar}>
        <Box className={classes.header}>
          {isLoading || delay ? (
            <Skeleton
              className={classes.skeletonHeader}
              variant="rect"
              height={26}
            />
          ) : (
            <Typography
              component="span"
              variant="h4"
              className={classes.headerLabel}
            >
              {header}
            </Typography>
          )}
          {!isLoading && !delay && (
            <ArrowSVG
              style={{
                transform: open ? 'rotate(180deg)' : '',
                transition: 'transform 200ms ease',
              }}
            />
          )}
        </Box>
        {isLoading || delay ? (
          <Skeleton className={classes.skeleton} variant="rect" height={42} />
        ) : (
          <Typography
            component="span"
            variant="body1"
            className={classes.subTitleLabel}
          >
            {open ? subTitle : subTitleCollapsed}
          </Typography>
        )}
      </Box>
      <Collapse
        onRest={({ isFullyOpened }) => {
          setCollapsed(isFullyOpened);
        }}
        isOpened={open}
      >
        <Box className={classes.content}>
          {isLoading || delay ? (
            <Box className={classes.loadingContainer}>
              <Lottie className={classes.loader} animationData={loader} loop />
              <Typography
                component="div"
                variant="body1"
                className={classes.loadingLabel}
              >
                {t('v2.global.loadingIndicator_label_loadingRecommendations')}
              </Typography>
            </Box>
          ) : (
            children
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default RecommendationBox;
